import { CloseCircleFilled } from "@ant-design/icons";
import {
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Spin,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import en from "antd/es/date-picker/locale/en_US";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { returnEllipsesFileName } from "src/constants/functions";
import { firstName } from "src/constants/add-variable";
import { IAddVariable } from "src/constants/types";
import { getCurrentDidId } from "src/store/selectors/features/currentDid";
import RequestAppAction from "src/store/slices/appActions";
import Colors from "src/themes/colors";
import Button from "../button";
import { TextifyModal } from "../modal";
import AddVariableBtn from "../modal-button-container/add-variable/AddVariableBtn";
import ModalButtonContainer from "../modal-button-container";
import AttachmentBtn from "../modal-button-container/AttachmentBtn";
import TemplateBtn from "../modal-button-container/TemplateBtn";
import useWindowDimensions from "src/hooks/useWindowDimensions";
import useFileUpload from "src/hooks/useFileUpload";

dayjs.extend(buddhistEra);

const { Text } = Typography;

const buddhistLocale = {
  ...en,
  lang: {
    ...en.lang,
    fieldDateFormat: "YYYY-MM-DD",
    fieldDateTimeFormat: "YYYY-MM-DD h:mm",
    yearFormat: "YYYY",
    cellYearFormat: "YYYY",
  },
} as typeof en;

interface props {
  modalRef: any;
  phoneNumbers: string[];
  setSelectedPhoneNumber: any;
  images: string[] | [];
  content: string;
  bulkContacts: any[];
}

export const AddBulkScheduleMessageModal = forwardRef(
  (
    {
      modalRef,
      phoneNumbers,
      setSelectedPhoneNumber,
      images,
      content,
      bulkContacts,
    }: props,
    ref
  ) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { width } = useWindowDimensions();
    const { setFiles, isFileUploading, success } = useFileUpload();
    const [form] = useForm();

    const textareaRef: any = useRef<any>(null);
    const uploadFileRef: any = useRef<HTMLInputElement | null>(null);

    const did = useSelector(getCurrentDidId);

    const [units, setUnits] = useState<number>(0);
    const [characters, setCharacters] = useState<number>(0);
    const [totalCharacters, setTotalCharacters] = useState<String>("");
    const [message, setMessage] = useState<string>("");
    const [variableList, setVariableList] = useState<IAddVariable[]>([]);
    const [attachments, setAttachments] = useState<any>([]);
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<
      string[] | []
    >([]);

    useEffect(() => {
      if (images?.length > 0) {
        setAttachments(images ?? []);
      }

      if (content) {
        form.setFieldValue("content", content);
        const charactersLength = content.length;
        const totalUnits = Math.floor(charactersLength / 160);
        const chr = charactersLength - (totalUnits || 0) * 160;

        if (charactersLength === 0 && attachments.length === 0) {
          setUnits(0);
        } else if (charactersLength > 0) {
          let val = totalUnits + 1 + attachments?.length * 3;
          setUnits(val);
        } else {
          let val = attachments?.length * 3;
          setUnits(val);
        }

        setCharacters(chr);
      }
    }, [images, content]);

    useImperativeHandle(ref, () => ({
      closeModal: () => {
        form.resetFields();
        setCharacters(0);
        setUnits(0);
        modalRef.current?.closeModal();
        setSelectedPhoneNumber([]);
      },
    }));

    useEffect(() => {
      if (phoneNumbers) {
        setSelectedPhoneNumbers(phoneNumbers);
      }
    }, [phoneNumbers]);

    useEffect(() => {
      const variables: IAddVariable[] = [];
      variables.push(firstName);
      setVariableList(variables);
    }, []);

    useEffect(() => {
      const charactersLength = totalCharacters.length;
      const totalUnits = Math.floor(charactersLength / 160);
      const chr = charactersLength - (totalUnits || 0) * 160;

      if (charactersLength === 0 && attachments.length === 0) {
        setUnits(0);
      } else if (charactersLength > 0) {
        let val = totalUnits + 1 + attachments?.length * 3;
        setUnits(val);
      } else {
        let val = attachments?.length * 3;
        setUnits(val);
      }

      setCharacters(chr);
    }, [totalCharacters, attachments]);

    const onTextChange = (value: string) => {
      setMessage(value);
      setTotalCharacters(value.trim());
    }

    const handleTextChange = async (e: any) => {
      onTextChange(e.target.value);
    };

    const handleCancel = () => {
      form.resetFields();
      setCharacters(0);
      setUnits(0);
      setTotalCharacters("");
      setMessage("");
      setAttachments([]);
      modalRef.current?.closeModal();
      setSelectedPhoneNumber([]);
    };
    const onSave = async (values: any) => {
      await form
        .validateFields()
        .then(() => {
          const updatedArr = Array.isArray(selectedPhoneNumbers)
            ? selectedPhoneNumbers?.map((i: string) =>
              i?.startsWith("+1")
                ? i
                : i?.startsWith("1") && i?.length === 11
                ? `+${i}`
                : `+1${i}`
            ) : [];

          const scheduledTime = new Date(values.scheduledTime);
          const data: {
            content: string;
            scheduledTime: string;
            fileLocations?: any[];
            phoneNumber: string[];
            variableList?: any[];
          } = {
            content: values.content,
            scheduledTime: scheduledTime.toISOString(),
            phoneNumber: updatedArr,
            variableList: variableList,
          };
          if (attachments.length > 0) {
            data["fileLocations"] = attachments;
          }
          dispatch(
            RequestAppAction.postBulkScheduleMessage({
              data: data,
              id: did,
              cbSuccess: () => {
                notification.success({
                  message: t("notification.saveScheduledSuccess"),
                });
                handleCancel();
                setAttachments([]);
              },
              cbFailure: (e: string) => {
                notification.success({
                  message: e,
                });
              },
            })
          );
        })
        .catch(() => {
          notification.error({ message: t("notification.saveScheduledFail") });
          return;
        });
    };

    const range = (start: any, end: any) =>
      Array.from({ length: end - start }, (_, i) => start + i);

    const disabledDateTime = (current: any) => {
      const currentDayjs = dayjs();

      // const isToday = dayjs().date();
      const isToday = currentDayjs.format('YYYY-MM-DD') === current.format('YYYY-MM-DD');

      return {
        disabledHours: () => (isToday ? range(0, currentDayjs?.hour()) : []),
        disabledMinutes: () =>
          isToday && currentDayjs?.hour() === current?.hour()
            ? range(0, currentDayjs.minute() + 1)
            : [],
      };
    };

    const handleMenuItemClick = (item: any) => {
      form.setFieldsValue({
        content: item.content,
      });
    };

    useEffect(() => {
      if (isFileUploading || !uploadFileRef?.current) {
        return;
      }
  
      if (success) {
        uploadFileRef.current.value = "";
        setAttachments((pre: any) => [...pre, ...success?.data]);
      } else {
        uploadFileRef.current.value = "";
      }
    }, [isFileUploading]);

    useEffect(() => {
      if (
        selectedPhoneNumbers.length !== 0 &&
        selectedPhoneNumbers.length < 1
      ) {
        form.resetFields();
        setCharacters(0);
        setUnits(0);
        modalRef.current?.closeModal();
        setSelectedPhoneNumber([]);
      }
    }, [selectedPhoneNumbers]);

    return (
      <div style={{ zIndex: 9999 }}>
        <TextifyModal ref={modalRef}>
          <input
            ref={uploadFileRef}
            onChange={(e) => {
              if (
                e?.target?.files
                && e.target.files.length > 0
              ) {
                setFiles(e.target.files);
              }
            }}
            style={{ display: "none" }}
            type="file"
            multiple
          />
          <Spin spinning={isFileUploading}>
            <Flex vertical>
              <Spin spinning={false}>
                <Form
                  onFinish={(values) => onSave(values)}
                  form={form}
                  name="scheduleForm"
                >
                  <div className="p-5 pb-0">
                    <div className="d-flex justify-content-between align-items-center pt-5 ">
                      <Form.Item
                        name="scheduledTime"
                        rules={[
                          { required: true, message: "Please Select Date" },
                        ]}
                      >
                        <DatePicker
                          showTime
                          disabledDate={(curr) =>
                            curr.isBefore(dayjs().subtract(1, "day"))
                          }
                          disabledTime={disabledDateTime}
                          locale={buddhistLocale}
                          use12Hours
                          format="YYYY-MM-DD h:mm A"
                          placeholder="Select Date and Time"
                        />
                      </Form.Item>
                    </div>
                    <div className="pb-5 row">
                      {Array.isArray(bulkContacts) && bulkContacts?.map((i) => (
                        <>
                          <Tag
                            closeIcon
                            onClose={() => {
                              setSelectedPhoneNumbers(
                                selectedPhoneNumbers.filter(
                                  (item) => item !== i?.phoneNumber
                                )
                              );
                            }}
                            style={{ borderRadius: 3 }}
                          >
                            {i?.name || i?.phoneNumber}
                          </Tag>
                        </>
                      ))}
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-5">
                      <Text className="font-size-16">
                        {t("sideBar.message")}
                      </Text>
                      <Text className="">
                        <div>
                          {t("sideBar.chars")} {characters}/160
                          <span className="pl-5">
                            {t("sideBar.msgUnits")} {units}/10
                          </span>
                        </div>
                      </Text>
                    </div>
                    <div
                      className="align-items-end pb-5"
                      style={{ position: "relative" }}
                    >
                      <Form.Item
                        name="content"
                        rules={[
                          // {
                          //   required: true,
                          //   message: "Please add some content",
                          // },
                          {
                            validator: (_, value) => {
                              if (!value && (!attachments || attachments.length === 0)) {
                                return Promise.reject(
                                  "Please add some content"
                                );
                              }
                              if (value && value.length > 1600) {
                                return Promise.reject(
                                  "Content length cannot exceed 1600 characters"
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input.TextArea
                          ref={textareaRef}
                          style={{ resize: "none" }}
                          rows={8}
                          placeholder="Type your text here..."
                          onChange={handleTextChange}
                          autoFocus
                        />
                      </Form.Item>
                      <ModalButtonContainer>
                        {width > 576 && (
                          <AddVariableBtn
                            disabled={!variableList || variableList.length === 0}
                            variableList={variableList}
                            form={form}
                            textareaRef={textareaRef}
                            message={message}
                            onTextChange={onTextChange}
                            style={{ borderRadius: 50, marginLeft: 10 }}
                          />
                        )}
                        <AttachmentBtn
                          uploadFileRef={uploadFileRef}
                          style={{ borderRadius: 50, marginLeft: 10 }}
                        />
                        <TemplateBtn
                          handleMenuItemClick={handleMenuItemClick}
                          style={{ borderRadius: 50, marginLeft: 10 }}
                        />
                      </ModalButtonContainer>
                    </div>
                  </div>
                  <Row>
                    <Col
                      style={{
                        display: "flex",
                      }}
                      span={24}
                    >
                      {Array.isArray(attachments) && attachments.map((i: string, ind: number) => (
                        <div
                          style={{
                            position: "relative",
                            marginRight: "0.5rem",
                          }}
                        >
                          <Tag
                            onClick={() => {
                              window.open(i, "_blank");
                            }}
                            style={{
                              background: Colors.white,
                              borderRadius: 4,
                            }}
                          >
                            <Text
                              key={ind}
                              className="font-size-14"
                              style={{
                                display: "flex",
                                cursor: "pointer  ",
                              }}
                            >
                              {returnEllipsesFileName(i)}
                            </Text>
                          </Tag>
                          <CloseCircleFilled
                            onClick={() => {
                              const arr = attachments;
                              setAttachments(
                                arr.filter((item: any) => item !== i)
                              );
                            }}
                            style={{
                              color: Colors.red,
                              position: "absolute",
                              top: -10,
                              right: 0,
                              zIndex: 2,
                            }}
                          />
                        </div>
                      ))}
                    </Col>
                  </Row>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "flex-end",
                      padding: "10px",
                    }}
                  >
                    <div style={{ marginLeft: "auto", marginRight: "10px" }}>
                      <Button
                        onBtnClick={handleCancel}
                        title={"Cancel"}
                        btnClass="white_btn"
                      />
                    </div>
                    <div>
                      {units > 10 ? (
                        <Tooltip
                          trigger={"hover"}
                          color="red"
                          title={t("message.unitError")}
                        >
                          <Button
                            title={"Save"}
                            disabled={true}
                            buttonType="button"
                          />
                        </Tooltip>
                      ) : (
                        <Button title={"Save"} buttonType="submit" />
                      )}
                    </div>
                  </div>
                </Form>
              </Spin>
            </Flex>
          </Spin>
        </TextifyModal>
      </div>
    );
  }
);
